import { athleteName, cleanupDanglingReferences, programmeLabel, starterName } from './workspace';
import { t } from '../i18n';
import { ageTierLabel, starterTypeLabel } from '../i18n/labels';
import type { Athlete, Club, Workspace } from '../types';

/**
 * What a coach picked in the import/export tree (`ui/import-export-view.ts`)
 * — one Set of ids per category. Every category is its own independent
 * list now (clubs, athletes, comps, starters, programs, entries) — an
 * `Entry` just references a compId/starterId/programId, the same way a
 * `Starter` references athleteIds, so picking one never pulls in another.
 */
export interface Selection {
  clubIds: Set<string>;
  athleteIds: Set<string>;
  compIds: Set<string>;
  starterIds: Set<string>;
  programIds: Set<string>;
  entryIds: Set<string>;
}

export function fullSelection(workspace: Workspace): Selection {
  return {
    clubIds: new Set(workspace.clubs.map((c) => c.id)),
    athleteIds: new Set(workspace.athletes.map((a) => a.id)),
    compIds: new Set(workspace.comps.map((c) => c.id)),
    starterIds: new Set(workspace.starters.map((s) => s.id)),
    programIds: new Set(workspace.programs.map((p) => p.id)),
    entryIds: new Set(workspace.entries.map((e) => e.id)),
  };
}

export interface ImportResult {
  workspace: Workspace;
  imported: { clubs: number; athletes: number; comps: number; starters: number; programs: number; entries: number };
  skipped: number;
}

/**
 * Merge the selected parts of `incoming` into `current`. An id already
 * present in `current` is left untouched and the incoming copy is skipped
 * (never overwritten, never duplicated under a new id) — the safe default
 * for re-importing a file you've imported before.
 */
/**
 * Merges `source` items selected by `selected` into `target`, skipping any
 * id already present in `target` (tracked via `existing`, since `target` is
 * being appended to in place as sibling categories are merged too — reading
 * ids off `target` itself mid-loop would work the same, `existing` just
 * avoids rebuilding that set on every push). Returns how many were actually
 * appended; the caller accumulates `skipped` across all categories, since
 * `ImportResult.skipped` is a single combined count, not one per category.
 */
function mergeCategory<T extends { id: string }>(
  source: readonly T[],
  target: T[],
  selected: ReadonlySet<string>,
  existing: ReadonlySet<string>,
): { imported: number; skipped: number } {
  let imported = 0;
  let skipped = 0;
  for (const item of source) {
    if (!selected.has(item.id)) continue;
    if (existing.has(item.id)) {
      skipped++;
      continue;
    }
    target.push(item);
    imported++;
  }
  return { imported, skipped };
}

export function applyImport(current: Workspace, incoming: Workspace, sel: Selection): ImportResult {
  const workspace: Workspace = {
    ...current,
    clubs: [...current.clubs],
    athletes: [...current.athletes],
    comps: [...current.comps],
    starters: [...current.starters],
    programs: [...current.programs],
    entries: [...current.entries],
  };

  const clubs = mergeCategory(incoming.clubs, workspace.clubs, sel.clubIds, new Set(current.clubs.map((c) => c.id)));
  const athletes = mergeCategory(incoming.athletes, workspace.athletes, sel.athleteIds, new Set(current.athletes.map((a) => a.id)));
  const comps = mergeCategory(incoming.comps, workspace.comps, sel.compIds, new Set(current.comps.map((c) => c.id)));
  const starters = mergeCategory(incoming.starters, workspace.starters, sel.starterIds, new Set(current.starters.map((s) => s.id)));
  const programs = mergeCategory(incoming.programs, workspace.programs, sel.programIds, new Set(current.programs.map((p) => p.id)));
  const entries = mergeCategory(incoming.entries, workspace.entries, sel.entryIds, new Set(current.entries.map((e) => e.id)));

  // Same cleanup parseWorkspace() applies on open: a reference that didn't end
  // up in the merged workspace (not selected, or skipped as a duplicate)
  // drops rather than points at nothing.
  cleanupDanglingReferences(workspace);

  return {
    workspace,
    imported: {
      clubs: clubs.imported,
      athletes: athletes.imported,
      comps: comps.imported,
      starters: starters.imported,
      programs: programs.imported,
      entries: entries.imported,
    },
    skipped: clubs.skipped + athletes.skipped + comps.skipped + starters.skipped + programs.skipped + entries.skipped,
  };
}

/** A filtered copy of `workspace` for a selective export — verbatim per
 *  category, no automatic dependency resolution (picking a starter doesn't
 *  pull in its athletes, programme or entries — same as picking files in
 *  Finder). */
export function buildExportWorkspace(workspace: Workspace, sel: Selection): Workspace {
  return {
    ...workspace,
    clubs: workspace.clubs.filter((c) => sel.clubIds.has(c.id)),
    athletes: workspace.athletes.filter((a) => sel.athleteIds.has(a.id)),
    comps: workspace.comps.filter((c) => sel.compIds.has(c.id)),
    starters: workspace.starters.filter((s) => sel.starterIds.has(s.id)),
    programs: workspace.programs.filter((p) => sel.programIds.has(p.id)),
    entries: workspace.entries.filter((e) => sel.entryIds.has(e.id)),
  };
}

export interface TreeItem {
  readonly id: string;
  readonly label: string;
}

/** Read-only projection of `source` into the tree categories, for the UI to
 *  render — labels only, no selection state. */
export function treeItems(source: Workspace): {
  clubs: readonly TreeItem[];
  athletes: readonly TreeItem[];
  comps: readonly TreeItem[];
  starters: readonly TreeItem[];
  programs: readonly TreeItem[];
  entries: readonly TreeItem[];
} {
  const clubs: readonly TreeItem[] = source.clubs.map((c: Club) => ({
    id: c.id,
    label: c.name || t('clubs.unnamed'),
  }));
  const athletes: readonly TreeItem[] = source.athletes.map((a: Athlete) => ({
    id: a.id,
    label: athleteName(a),
  }));
  const comps: readonly TreeItem[] = source.comps.map((comp) => ({
    id: comp.id,
    label: comp.name || t('competitions.unnamed'),
  }));
  const starters: readonly TreeItem[] = source.starters.map((s) => ({
    id: s.id,
    label: t('ie.item.starter', { name: starterName(source, s), type: starterTypeLabel(s.type) }),
  }));
  const programs: readonly TreeItem[] = source.programs.map((p) => ({
    id: p.id,
    label: t('ie.item.program', {
      name: programmeLabel(p, starterTypeLabel(p.type), ageTierLabel(p.ageTier)),
      count: p.program.length,
    }),
  }));
  const entries: readonly TreeItem[] = source.entries.map((e) => {
    const comp = source.comps.find((c) => c.id === e.compId);
    const starter = source.starters.find((s) => s.id === e.starterId);
    return {
      id: e.id,
      label: t('ie.item.entry', {
        comp: comp ? comp.name || t('competitions.unnamed') : t('entries.unset'),
        starter: starter ? starterName(source, starter) : t('entries.unset'),
      }),
    };
  });
  return { clubs, athletes, comps, starters, programs, entries };
}
